// src/pages/BcpWeatherInfo.jsx
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import api from "../lib/api";
import './BcpWeatherInfo.css'; 

const WeatherInfo = () => {
  const location = useLocation();
  const navigate = useNavigate();
  

  const { lat, lng } = location.state || { lat: null, lng: null };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weatherData, setWeatherData] = useState({
    home: null,       
    current: null,     
    prefectures_list: [],
    isAtHome: false
  });

  useEffect(() => {
    const fetchWeatherData = async () => {
      try {
        setLoading(true);
     
        const response = await api.get(`/weather-bcp/?lat=${lat}&lng=${lng}`);
        const data = response.data;

        setWeatherData({
          home: data.home_weather,
          current: data.current_weather,
          prefectures_list: data.prefectures_list,
          isAtHome: data.is_at_home
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWeatherData();
  }, [lat, lng]);


  const getLevelStyle = (level) => {
    const lvl = parseInt(level, 10) || 1; 
    switch (lvl) {
      case 5: return { class: 'level-5-black', text: 'レベル5: 特別警報 (緊急安全確保)' };
      case 4: return { class: 'level-4-purple', text: 'レベル4: 危険警報 (避難指示)' };
      case 3: return { class: 'level-3-red', text: 'レベル3: 警報 (高齢者等避難)' };
      case 2: return { class: 'level-2-yellow', text: 'レベル2: 注意報 (大雨/洪水注意報)' };
      default: return { class: 'level-1-white', text: 'レベル1: 早期注意情報 (通常)' };
    }
  };

  
  const homeLevel = parseInt(weatherData.home?.max_level, 10) || 1;
  const currentLevel = parseInt(weatherData.current?.max_level, 10) || 1;

  if (loading) {
    return (
      <div className="weather-info-page loading-box">
        <div className="spinner"></div>
        <p>気象庁から最新の防災情報を取得中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="weather-info-page error-box">
        <p>エラーが発生しました: {error}</p>
        <button className="bcp-btn-back" onClick={() => navigate(-1)}>戻る</button>
      </div>
    );
  }

  return (
    <div className="weather-info-page">
      <header className="weather-header">
        <h3>防災気象情報 (BCP管理)</h3>
        <p className="refresh-note">
          ※ 気象庁<br />（JMA）のリアルタイムデータと連携しています。 
        </p>
      </header>

      <div className="weather-container">
        
        {/* BLOCK 1: 登録住所（自宅周辺） */}
        <section className="weather-card home-card">
          <div className="card-header">
            <h3>🏠 登録住所（自宅周辺）</h3>
            <span className="area-tag">{`${weatherData.home?.prefecture || '大阪府'}${weatherData.home?.city || ''}`}</span>
          </div>
          <div className={`level-badge ${getLevelStyle(weatherData.home?.max_level).class}`}>
            {getLevelStyle(weatherData.home?.max_level).text}
          </div>
          <div className="card-body">
            <h4>発令中の警報・注意報:</h4>
            <ul className="warning-list">
              {weatherData.home?.warnings && weatherData.home.warnings.length > 0 ? (
                weatherData.home.warnings.map((warn, index) => <li key={index}>⚠️ {warn}</li>)
              ) : (
                <li className="no-warning">🟢 現在、警報・注意報はありません。</li>
              )}
            </ul>
            
          
            {weatherData.isAtHome && homeLevel >= 4 && (
              <div className="shelter-link">
                <Link to="/bcp/home-shelter" className="btn-shelter"> 
                 お近くの避難所情報
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* BLOCK 2: 現在地の情報 */}
        {!weatherData.isAtHome && (
          <section className="weather-card current-card">
            <div className="card-header">
              <h3>📍 現在地の情報</h3>
              <span className="area-tag">
           
                {weatherData.current?.prefecture ? `${weatherData.current.prefecture}${weatherData.current.city || ''}` : (lat && lng ? '位置特定中' : 'GPS未設定')}
              </span>
            </div>

           
            {weatherData.current?.prefecture && weatherData.current?.max_level ? (
              <>
                <div className={`level-badge ${getLevelStyle(weatherData.current?.max_level).class}`}>
                  {getLevelStyle(weatherData.current?.max_level).text}
                </div>
                <div className="card-body">
                  <h4>発令中の警報・注意報:</h4>
                  <ul className="warning-list">
                    {weatherData.current?.warnings && weatherData.current.warnings.length > 0 ? (
                      weatherData.current.warnings.map((warn, index) => <li key={index}>⚠️ {warn}</li>)
                    ) : (
                      <li className="no-warning">🟢 現在、警報・注意報はありません。</li>
                    )}
                  </ul>
                  {currentLevel >= 4 && (
                    <div className="shelter-link">
                      <Link to="/bcp/nearby-shelter" className="btn-shelter"> 
                      お近くの避難所情報
                      </Link>
                    </div>
                  )}
                </div>
              </>
            ) : (
              
              <div className="card-body gps-disabled">
                <p>📴 位置情報（GPS）から気象情報を取得できませんでした。</p>
                <p className="sub-text">出張先や外出先の警告を確認したい場合は、端末のGPSをONにするか、下のエリア選択から手動で切り替えてください。</p>
                
                <select 
                  className="pref-selector" 
                  onChange={(e) => navigate('.', { state: { lat: 'manual', lng: e.target.value } })}
                >
                  <option value="">-- 都道府県を手動で選択 --</option>
                  {weatherData.prefectures_list?.map((pref) => (
                    <option key={pref.code} value={pref.code}>
                      {pref.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </section>
        )}
      </div>

      <div className="action-footer">
        <button type="button" className="bcp-btn-back" onClick={() => navigate(-1)}>
          戻る
        </button>
      </div>
    </div>
  );
};

export default WeatherInfo;